import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitle } from '../../shared/hooks/useTitle';
import api from '../../shared/services/api/api';
import { AuthB2cContext } from '../context/AuthB2cContext';
import { removeItem } from '../../shared/services/storage/storage';
import { STORAGE_KEY } from '../../shared/config';
import Loading from '../components/Loading';
import PageContainer from '../components/PageContainer';
import NavigationButtons from '../components/NavigationButtons';
import { useHotkeys } from 'react-hotkeys-hook';
import NotValidLink from '../components/NotValidLink';
import PageHeader from '../components/PageHeader';
import { DATA_ENTRY_FIRST_STEP_ROUTE, WELCOME_KEY, createPathForRoute } from '../constants/baseRoutePaths';
import { scrollToConf } from '../constants/scrollToConf';
import { pageSubtitles, pageTitles } from '../constants/fields';

const Welcome = () => {
  const { username = '', token = '' } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const authB2cContext = useContext(AuthB2cContext);
  const user = authB2cContext?.user;
  const name = user?.name || '';
  const capitalizedName = name?.charAt(0)?.toUpperCase() + name?.toLowerCase().slice(1);
  const title = pageTitles[WELCOME_KEY];
  const subtitle = typeof pageSubtitles[WELCOME_KEY] !== 'string' && pageSubtitles[WELCOME_KEY](capitalizedName);

  useTitle(title);

  const handleNext = () => {
    window.scrollTo(scrollToConf);
    navigate(createPathForRoute(DATA_ENTRY_FIRST_STEP_ROUTE));
  };

  const handlePassword = () => {
    navigate(`/forgot?backTo=${encodeURIComponent(location.href)}`);
  };

  useHotkeys('enter', () => (user?.hasPassword ? handleNext() : handlePassword()));

  useEffect(() => {
    setLoading(true);
    const checkUser = async () => {
      const response = await api.verify({ username, token }).catch((error) => {
        console.error(error);
        setLoading(false);
      });
      removeItem(STORAGE_KEY.B2C_STATE);
      if (response.status === 200 && response?.access_token) {
        authB2cContext?.setUser({
          name: response.user.name,
          surname: response.user.surname,
          hasPassword: response.hasPassword,
          access_token: response.access_token,
          visitId: response.visita_id,
        });
      } else {
        authB2cContext?.setUser({
          name: '',
          surname: '',
          hasPassword: false,
          access_token: '',
          visitId: 0,
        });
      }
      setLoading(false);
    };
    if (username !== '' || token !== '') {
      checkUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (user === null || user?.access_token === null || user?.access_token === '' || username === '' || token === '') {
    return <NotValidLink />;
  }

  return (
    <PageContainer>
      <>
        <PageHeader title={title} subtitle={subtitle || ''} />
        <p className="text-xl"></p>
        <p>
          Infatti il referto che riceverai dopo aver effettuato il test terrà conto non solo del tuo patrimonio
          genetico, ma anche dei tuoi problemi di salute e dei farmaci che assumi.
        </p>
        <p>
          Innanzitutto se non l&apos;hai ancora fatto, ti chiederemo di scegliere una password e di inserire la tua
          emali ed il tuo codice fiscale.
          <br />
          Poi ti faremo alcune semplici domande e, se vorrai, potrai prenotare il corriere per il ritiro del campione.
        </p>
        <p>Il tutto richiederà circa 10 minuti.</p>
        <p>
          Ma attenzione, una volta inviati i dati non potrai più modificarli da qui.
          {/* , dovrai farlo dalla nostra app{' '}
          <a href="https://www.youmeals.it">Youmeals</a>. */}
        </p>
        <NavigationButtons
          prev={{
            condition: false,
            label: '',
            action: () => {},
          }}
          next={{
            label: user?.hasPassword ? 'Inizia' : 'Richiedi una password',
            action: user?.hasPassword ? handleNext : handlePassword,
            color: user?.hasPassword ? 'success' : undefined,
          }}
        />
      </>
    </PageContainer>
  );
};

export default Welcome;
